import {
    afterNextRender,
    Directive,
    ElementRef,
    EventEmitter,
    HostListener,
    Input,
    type OnChanges,
    type OnDestroy,
    Output,
    type SimpleChanges,
    inject,
    DOCUMENT,
} from '@angular/core';

import { fromEvent, type Subscription } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import type { PanelPosition } from '@common/types/layout.type';

const DEFAULT_BOUNDARY: string = 'body';
const BOTTOM_GAP: number = 10;

export type PanelBounds = { minX: number; maxX: number; minY: number; maxY: number };

/**
 * Clamps a panel offset into bounds. The top bound is applied last, so when the panel is taller than
 * the window its header (drag handle and close button) stays visible and the bottom overflows instead.
 */
export function clampPanelPosition({ x, y }: PanelPosition, { minX, maxX, minY, maxY }: PanelBounds): PanelPosition {
    return {
        x: Math.max(minX, Math.min(maxX, x)),
        y: Math.max(minY, Math.min(maxY, y)),
    };
}

/**
 * Drags the panel by its `[spPanelDragHandler]`. At rest the transform is owned by the panel's template
 * (bound to `panelPosition`); the directive writes it only while dragging and reports every settled
 * position through `panelPositionChange`, always already clamped to what is visible on screen.
 */
@Directive({ selector: '[spPanelDrag]' })
export class SpPanelDragDirective implements OnChanges, OnDestroy {
    private readonly elementRef = inject(ElementRef);
    private readonly document = inject<Document>(DOCUMENT);

    @Input() public draggingBoundary: string = DEFAULT_BOUNDARY;
    @Input() public spPanelDragTarget: string = null;
    @Input() public panelPosition: PanelPosition | null = null;
    @Output() public panelPositionChange = new EventEmitter<PanelPosition>();

    private element: HTMLElement;
    private handleElement: HTMLElement;
    private draggingBoundaryElement: HTMLElement | HTMLBodyElement;
    private subscriptions: Subscription[] = [];
    private position: PanelPosition = { x: 0, y: 0 };
    private isDragging: boolean = false;
    private resizeFrame: number = null;
    private observer: MutationObserver;
    private processing: boolean = false;

    public ngOnChanges(changes: SimpleChanges): void {
        if (changes.panelPosition && this.panelPosition && !this.isDragging) {
            this.position = this.panelPosition;
        }
    }

    constructor() {
        // The settings panel is attached through a portal after the first render, so in
        // `ngAfterViewInit` the target is not in the document yet.
        afterNextRender(() => this.init());
    }

    private init(): void {
        this.draggingBoundaryElement = this.document.querySelector(this.draggingBoundary) ?? this.document.body;

        this.element = this.spPanelDragTarget
            ? this.document.querySelector(this.spPanelDragTarget)
            : this.elementRef.nativeElement;

        if (!this.element) {
            return;
        }

        this.handleElement = this.element.querySelector('[spPanelDragHandler]') || this.element;

        this.initDrag();
        // The panel's own template applies the stored transform during the next check
        setTimeout(() => this.keepInView());

        this.observer = new MutationObserver((mutations) => {
            mutations.forEach(() => {
                if (!this.processing) {
                    this.processing = true;
                    setTimeout(() => {
                        this.processing = false;
                        this.unsubscribe();
                        this.handleElement = this.element.querySelector('[spPanelDragHandler]') || this.element;
                        this.initDrag();
                        // Switching settings changes the panel height
                        this.keepInView();
                    });
                }
            });
        });

        this.observer.observe(this.element, {
            childList: true,
            subtree: true,
        });
    }

    @HostListener('window:resize')
    public onWindowResize(): void {
        cancelAnimationFrame(this.resizeFrame);
        this.resizeFrame = requestAnimationFrame(() => this.keepInView());
    }

    private initDrag(): void {
        const dragStart$ = fromEvent<MouseEvent>(this.handleElement, 'mousedown');
        const dragEnd$ = fromEvent<MouseEvent>(this.document, 'mouseup');
        const drag$ = fromEvent<MouseEvent>(this.document, 'mousemove').pipe(takeUntil(dragEnd$));

        let dragSub: Subscription;

        const dragStartSub = dragStart$.subscribe((startEvent: MouseEvent) => {
            this.isDragging = true;
            const bounds = this.measureBounds();
            const start = this.position;

            this.handleElement.classList.add('dragging');

            dragSub = drag$.subscribe((dragEvent: MouseEvent) => {
                dragEvent.preventDefault();

                this.position = clampPanelPosition(
                    {
                        x: start.x + dragEvent.clientX - startEvent.clientX,
                        y: start.y + dragEvent.clientY - startEvent.clientY,
                    },
                    bounds
                );
                this.element.style.transform = `translate3d(${this.position.x}px, ${this.position.y}px, 0)`;
            });
        });

        const dragEndSub = dragEnd$.subscribe(() => {
            this.handleElement.classList.remove('dragging');

            if (dragSub) {
                dragSub.unsubscribe();
            }

            if (this.isDragging) {
                this.isDragging = false;
                this.panelPositionChange.emit(this.position);
            }
        });

        this.subscriptions.push(dragStartSub, dragEndSub);
    }

    /** Pulls the panel back into view (after opening, a content change or a window resize). */
    private keepInView(): void {
        if (this.isDragging || !this.element) {
            return;
        }

        const current = this.position;
        this.position = clampPanelPosition(current, this.measureBounds());

        if (this.position.x !== current.x || this.position.y !== current.y) {
            this.panelPositionChange.emit(this.position);
        }
    }

    /**
     * Offsets the panel may take: its top stays below the editor header (the top of the boundary, which
     * already follows `--navbar-height`), the rest stays inside the viewport.
     */
    private measureBounds(): PanelBounds {
        const boundary = this.draggingBoundaryElement.getBoundingClientRect();
        const panel = this.element.getBoundingClientRect();
        // The handle sticks out of the panel, so it has to fit too
        const handle = this.handleElement.getBoundingClientRect();
        const { clientWidth, clientHeight } = this.document.documentElement;
        // Rect of the panel with its handle, without the current offset
        const left = Math.min(panel.left, handle.left) - this.position.x;
        const right = Math.max(panel.right, handle.right) - this.position.x;
        const top = Math.min(panel.top, handle.top) - this.position.y;
        const bottom = Math.max(panel.bottom, handle.bottom) - this.position.y;

        return {
            minX: -left,
            maxX: clientWidth - right,
            minY: boundary.top - top,
            maxY: clientHeight - BOTTOM_GAP - bottom,
        };
    }

    private unsubscribe(): void {
        this.subscriptions.forEach((subscription: Subscription) => subscription?.unsubscribe());
        this.subscriptions = [];
    }

    public ngOnDestroy(): void {
        cancelAnimationFrame(this.resizeFrame);
        this.unsubscribe();
        this.observer?.disconnect();
    }
}
